import React, { useEffect, useState } from "react";
import { Form, Input, Select, Switch, InputNumber, Button, Upload, Radio, message, Alert, ColorPicker, DatePicker } from "antd";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faArrowLeft, faImage, faArrowUpRightFromSquare, faLink } from "@fortawesome/free-solid-svg-icons";
import utilityBlockApi from "../../../api/utilityBlockApi";
import { AudienceFields, audiencePayload } from "../../../components/audience-fields";
import { getUtilityIcon, UTILITY_ICON_NAMES } from "../../../shared/utility-icon-map";

const API_IMG_URL = process.env.REACT_APP_API_URL?.replace("/api/v1", "") || "";
const FA_GALLERY_URL = "https://fontawesome.com/search?o=r&m=free&s=solid,brands";
const ICON_PREVIEW_FALLBACK_COLOR = "#EE4D2D";
const ICON_PREVIEW_FALLBACK_BG = "#FFF2F0";

// Presets cho ColorPicker — màu icon đậm & màu nền nhạt (khớp tone app).
const ICON_COLOR_PRESETS = ["#EE4D2D", "#DC2626", "#EA580C", "#D97706", "#16A34A", "#0EA5E9", "#2563EB", "#7C3AED", "#DB2777", "#1877F2", "#111827"];
const ICON_BG_PRESETS = ["#FFF2F0", "#FEE2E2", "#FFEDD5", "#FEF3C7", "#D1FAE5", "#E0F2FE", "#EDE9FE", "#FCE7F3", "#E7F0FE", "#F3F4F6"];
const BADGE_PRESETS = ["HOT", "NEW", "SALE", "HOT🔥", "-50%"];
// Màn hình app cho phép mở qua mode 'screen' — PHẢI khớp SCREEN_WHITELIST ở mobile
// (open-utility-block.ts) và route trong MainStackParamList.
const SCREEN_OPTIONS = [
    { value: "Vouchers", label: "Đổi Voucher" },
    { value: "Referral", label: "Giới thiệu bạn" },
    { value: "Cashback", label: "Hoàn tiền" },
    { value: "MyOrders", label: "Đơn của tôi" },
    { value: "PriceWatch", label: "Theo dõi giá" },
    { value: "LinkConverter", label: "Rải link" },
    { value: "ShopeeVideo", label: "Video độc quyền" },
    { value: "Withdraw", label: "Rút tiền" },
    { value: "Leaderboard", label: "Bảng xếp hạng" },
    { value: "SavedProducts", label: "Sản phẩm đã lưu" },
    { value: "Notifications", label: "Thông báo" },
    { value: "Settings", label: "Cài đặt" },
    { value: "Help", label: "Trợ giúp" },
    { value: "CommunityFeed", label: "Kênh Cộng Đồng" },
];

/** ColorPicker trả AggregationColor (hoặc string khi load) → chuẩn hoá về hex string. */
function toHex(v) {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v.toHexString === "function") return v.toHexString();
    return "";
}

function UtilityBlockForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [iconSource, setIconSource] = useState("image"); // 'image' | 'fontawesome'
    const [fileList, setFileList] = useState([]);
    const [previewUrl, setPreviewUrl] = useState("");

    // Theo dõi giá trị icon để preview trực tiếp khi gõ/đổi màu.
    const watchIconName = Form.useWatch("iconName", form);
    const watchIconColor = Form.useWatch("iconColor", form);
    const watchIconBg = Form.useWatch("iconBg", form);
    const watchBadge = Form.useWatch("badge", form);
    const watchOpenMode = Form.useWatch("openMode", form);
    const isScreenMode = watchOpenMode === "screen";
    const previewIcon = getUtilityIcon(watchIconName);
    const iconSupported = Boolean(previewIcon);

    useEffect(() => {
        if (isEdit) loadBlock();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadBlock = async () => {
        try {
            const { data } = await utilityBlockApi.getAll();
            const b = (data.data || []).find((x) => x.id === Number(id));
            if (!b) {
                message.error("Tiện ích không tồn tại");
                navigate("/utility-block");
                return;
            }
            setIconSource(b.imagePath ? "image" : "fontawesome");
            if (b.imagePath) setPreviewUrl(`${API_IMG_URL}${b.imagePath}`);
            form.setFieldsValue({
                title: b.title, link: b.link, openMode: b.openMode, screenName: b.screenName,
                injectUserId: b.injectUserId, requireLogin: b.requireLogin, priority: b.priority, isActive: b.isActive,
                iconName: b.iconName, iconColor: b.iconColor, iconBg: b.iconBg, badge: b.badge,
                groupName: b.groupName,
                notifyUserIds: b.notifyUserIds || "",
                audience: b.audience || "all", audienceDays: b.audienceDays ?? null,
                startAt: b.startAt ? dayjs.unix(b.startAt) : null,
                endAt: b.endAt ? dayjs.unix(b.endAt) : null,
            });
        } catch {
            message.error("Không thể tải thông tin tiện ích");
        }
    };

    const handleFileChange = ({ fileList: newList }) => {
        setFileList(newList);
        if (newList.length > 0 && newList[0].originFileObj) {
            setPreviewUrl(URL.createObjectURL(newList[0].originFileObj));
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const fd = new FormData();
            fd.append("title", values.title);
            fd.append("link", values.link || "");
            fd.append("openMode", values.openMode || "webview");
            fd.append("screenName", values.screenName || "");
            fd.append("injectUserId", values.injectUserId ? "true" : "false");
            fd.append("requireLogin", values.requireLogin ? "true" : "false");
            fd.append("priority", String(values.priority || 0));
            fd.append("badge", values.badge || "");
            fd.append("groupName", values.groupName || "");
            fd.append("notifyUserIds", values.notifyUserIds || "");
            const audience = audiencePayload(values);
            fd.append("audience", audience.audience);
            fd.append("audienceDays", audience.audienceDays);
            fd.append("startAt", values.startAt ? String(values.startAt.unix()) : "");
            fd.append("endAt", values.endAt ? String(values.endAt.unix()) : "");
            if (isEdit) fd.append("isActive", values.isActive !== false ? "true" : "false");

            if (iconSource === "image") {
                // Dùng ảnh → xoá iconName để không lẫn
                fd.append("iconName", "");
                if (fileList.length > 0 && fileList[0].originFileObj) {
                    fd.append("image", fileList[0].originFileObj);
                } else if (!previewUrl) {
                    // Không có ảnh mới VÀ không có ảnh cũ (vd vừa đổi từ FontAwesome sang Ảnh)
                    message.error("Vui lòng chọn icon ảnh");
                    setLoading(false);
                    return;
                }
            } else {
                // Dùng FontAwesome → xoá ảnh cũ (gửi imagePath rỗng) + set icon
                fd.append("imagePath", "");
                fd.append("iconName", values.iconName || "");
                fd.append("iconColor", toHex(values.iconColor));
                fd.append("iconBg", toHex(values.iconBg));
                if (!values.iconName) {
                    message.error("Vui lòng nhập tên icon FontAwesome");
                    setLoading(false);
                    return;
                }
            }

            if (isEdit) {
                await utilityBlockApi.update(Number(id), fd);
                message.success("Cập nhật thành công");
            } else {
                await utilityBlockApi.create(fd);
                message.success("Tạo tiện ích thành công");
            }
            navigate("/utility-block");
        } catch (error) {
            message.error(error.response?.data?.message || "Có lỗi xảy ra");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 w-full max-w-4xl">
            <div className="flex items-center gap-3 mb-5">
                <Button icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/utility-block")} />
                <h1 className="text-2xl font-bold">{isEdit ? "Sửa tiện ích" : "Tạo tiện ích mới"}</h1>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit}
                initialValues={{ openMode: "webview", injectUserId: false, requireLogin: false, priority: 0, isActive: true, audience: "all" }}>
                <Form.Item label="Tên ngắn" name="title" rules={[{ required: true, message: "Nhập tên hiển thị" }]}>
                    <Input placeholder="VD: Shopee x FB" />
                </Form.Item>

                <Form.Item label="Loại icon">
                    <Radio.Group value={iconSource} onChange={(e) => setIconSource(e.target.value)}>
                        <Radio value="image">Ảnh</Radio>
                        <Radio value="fontawesome">FontAwesome</Radio>
                    </Radio.Group>
                </Form.Item>

                {iconSource === "image" ? (
                    <Form.Item label="Icon ảnh (vuông)"
                        extra="Ảnh sẽ được cắt vuông 128x128. Nhận cả GIF động (tối đa 120 khung) — app sẽ chạy animation. Đừng lạm dụng ảnh động: nhiều ô cùng chạy sẽ tốn pin máy yếu.">
                        <div className="flex items-center gap-3">
                            {previewUrl ? (
                                <img src={previewUrl} alt="preview"
                                    style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb" }} />
                            ) : (
                                <div style={{ width: 64, height: 64, borderRadius: 10, border: "1px dashed #d1d5db", background: "rgb(var(--surface-sunken))" }}
                                    className="flex items-center justify-center">
                                    <FontAwesomeIcon icon={faImage} className="text-content-3 text-xl" />
                                </div>
                            )}
                            <Upload maxCount={1} fileList={fileList} onChange={handleFileChange}
                                beforeUpload={() => false} accept="image/jpeg,image/png,image/webp,image/gif"
                                showUploadList={false}>
                                <Button icon={<FontAwesomeIcon icon={faUpload} className="mr-2" />}>
                                    {previewUrl ? "Thay ảnh" : "Chọn ảnh"}
                                </Button>
                            </Upload>
                        </div>
                    </Form.Item>
                ) : (
                    <>
                        <div className="flex gap-4 items-start">
                            {/* Preview icon trực tiếp (đúng như app render) */}
                            <Form.Item label="Xem trước">
                                <div
                                    style={{
                                        width: 56, height: 56, borderRadius: 12,
                                        background: toHex(watchIconBg) || ICON_PREVIEW_FALLBACK_BG,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        border: "1px solid #e5e7eb",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={previewIcon || faLink}
                                        style={{ color: toHex(watchIconColor) || ICON_PREVIEW_FALLBACK_COLOR, fontSize: 22 }}
                                    />
                                </div>
                            </Form.Item>

                            <Form.Item
                                label="Tên icon (FontAwesome)"
                                name="iconName"
                                className="flex-1"
                                validateStatus={watchIconName && !iconSupported ? "warning" : ""}
                                help={
                                    watchIconName && !iconSupported
                                        ? "App chưa hỗ trợ icon này → sẽ hiển thị icon mặc định. Chọn từ danh sách bên dưới."
                                        : undefined
                                }
                                extra={
                                    <a href={FA_GALLERY_URL} target="_blank" rel="noreferrer">
                                        Xem danh sách icon FontAwesome{" "}
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-1" />
                                    </a>
                                }
                            >
                                <Input placeholder="faBolt" />
                            </Form.Item>

                            <Form.Item label="Màu icon" name="iconColor">
                                <ColorPicker
                                    format="hex"
                                    allowClear
                                    showText
                                    presets={[{ label: "Màu icon", colors: ICON_COLOR_PRESETS }]}
                                />
                            </Form.Item>
                            <Form.Item label="Màu nền" name="iconBg">
                                <ColorPicker
                                    format="hex"
                                    allowClear
                                    showText
                                    presets={[{ label: "Màu nền", colors: ICON_BG_PRESETS }]}
                                />
                            </Form.Item>
                        </div>

                        {/* Bộ chọn nhanh — chỉ các icon app hỗ trợ */}
                        <Form.Item label="Chọn nhanh (app hỗ trợ)">
                            <div className="flex flex-wrap gap-2">
                                {UTILITY_ICON_NAMES.map((name) => {
                                    const active = name === watchIconName;
                                    return (
                                        <button
                                            type="button"
                                            key={name}
                                            title={name}
                                            onClick={() => form.setFieldsValue({ iconName: name })}
                                            style={{
                                                width: 40, height: 40, borderRadius: 8, cursor: "pointer",
                                                background: active ? "#FFF2F0" : "#f9fafb",
                                                border: active ? "1.5px solid #EE4D2D" : "1px solid #e5e7eb",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={getUtilityIcon(name)}
                                                style={{ color: active ? "#EE4D2D" : "#6b7280", fontSize: 16 }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </Form.Item>
                    </>
                )}

                {isScreenMode ? (
                    <Form.Item label="Màn hình app" name="screenName"
                        rules={[{ required: true, message: "Chọn màn hình app" }]}
                        extra="Mở thẳng màn hình trong app khi nhấn.">
                        <Select placeholder="Chọn màn hình" options={SCREEN_OPTIONS} />
                    </Form.Item>
                ) : (
                    <Form.Item label="Link" name="link"
                        rules={[{ required: true, message: "Nhập link" }]}
                        extra="Chèn {userId} để gắn user hiện tại. VD: https://fb.longhousee.me/?user_id={userId}">
                        <Input placeholder="https://..." />
                    </Form.Item>
                )}

                <div className="flex gap-4">
                    <Form.Item label="Cách mở" name="openMode" className="flex-1"
                        extra="App view / Web view / Trình duyệt ngoài / Mở màn hình app nội bộ">
                        <Select>
                            <Select.Option value="appview">App view (WebView nhúng)</Select.Option>
                            <Select.Option value="webview">Web view (trong app)</Select.Option>
                            <Select.Option value="external">Trình duyệt ngoài</Select.Option>
                            <Select.Option value="screen">Mở màn hình app</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Thứ tự" name="priority" className="w-28">
                        <InputNumber min={0} className="w-full" placeholder="0 = đầu" />
                    </Form.Item>
                </div>

                <Form.Item label="Badge (nhãn góc)" name="badge"
                    extra="Để trống = không hiện. VD: HOT, NEW, -50%">
                    <Input placeholder="HOT" maxLength={16} allowClear style={{ maxWidth: 220 }} />
                </Form.Item>
                <Form.Item label="Nhóm hiển thị" name="groupName"
                    extra='Tiêu đề nhóm ở màn "Tất cả tiện ích" trong app. Để trống = nhóm "Khám phá thêm".'>
                    <Input placeholder="Khám phá thêm" maxLength={40} allowClear style={{ maxWidth: 260 }} />
                </Form.Item>

                <Form.Item label="Chọn nhanh badge">
                    <div className="flex flex-wrap gap-2">
                        {BADGE_PRESETS.map((b) => (
                            <Button key={b} size="small"
                                type={watchBadge === b ? "primary" : "default"}
                                onClick={() => form.setFieldsValue({ badge: b })}>
                                {b}
                            </Button>
                        ))}
                        <Button size="small" danger onClick={() => form.setFieldsValue({ badge: "" })}>
                            Bỏ badge
                        </Button>
                    </div>
                </Form.Item>

                <div className="flex gap-4">
                    <Form.Item label="Bắt đầu hiện" name="startAt" className="w-1/2"
                        extra="Để trống = luôn hiện. App tự ẩn/hiện theo giờ.">
                        <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" placeholder="Luôn hiện" />
                    </Form.Item>
                    <Form.Item label="Kết thúc" name="endAt" className="w-1/2"
                        extra="Để trống = không giới hạn.">
                        <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" placeholder="Không giới hạn" />
                    </Form.Item>
                </div>

                <AudienceFields extra="App/web tự ẩn ô này với người không đúng đối tượng. Bản app cũ (chưa cập nhật) vẫn thấy đủ ô." />

                <Form.Item label="Báo yêu cầu mới cho userId" name="notifyUserIds"
                    extra='Ai nhận noti "N yêu cầu đang chờ" của tiện ích này. Nhập userId cách nhau dấu phẩy, tối đa 20 — ĐIỀN VÀO ĐÂY LÀ CHỐT: gỡ userId nào ra thì người đó thôi nhận. Để trống = lùi về admin/mod đã tick quyền ở trang Nhân sự (chưa ai tick thì mọi admin nhận). Chỉ áp dụng cho tiện ích có báo về qua API (VD: Link kích mã YT).'>
                    <Input placeholder="VD: 14, 8022, 26364" allowClear style={{ maxWidth: 420 }} />
                </Form.Item>

                <div className="flex gap-6">
                    <Form.Item label="Gắn userId" name="injectUserId" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item label="Cần đăng nhập" name="requireLogin" valuePropName="checked"
                        tooltip="Chưa đăng nhập sẽ bị đẩy sang màn Login thay vì mở link">
                        <Switch />
                    </Form.Item>
                    {isEdit && (
                        <Form.Item label="Active" name="isActive" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    )}
                </div>

                <Form.Item shouldUpdate={(p, c) => p.injectUserId !== c.injectUserId} noStyle>
                    {({ getFieldValue }) =>
                        getFieldValue("injectUserId") ? (
                            <Alert type="warning" showIcon className="mb-4"
                                message="Chỉ bật gắn userId cho domain bạn kiểm soát — userId sẽ lộ qua URL." />
                        ) : null
                    }
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} className="mr-2">
                        {isEdit ? "Cập nhật" : "Tạo tiện ích"}
                    </Button>
                    <Button onClick={() => navigate("/utility-block")}>Huỷ</Button>
                </Form.Item>
            </Form>
        </div>
    );
}

export default UtilityBlockForm;
